import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';

import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import cn from 'classnames';

import { AutoSizer } from 'react-virtualized-auto-sizer';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';

import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import { MdError, MdTv, MdHeadset, MdImage, MdRadio } from 'react-icons/md';
import { FiCast } from 'react-icons/fi';

import { Placement, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { StickyTable, CellRenderer } from '@Components/StickyTable';
import { Img } from '@Components/Img';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { FilterBar } from '@Components/FilterBar';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';

import { useSetError } from '../../redux/modals';
import { ToolsLambdaFetch, awaitJSON, SheetsLambdaFetch } from '../../utils/fetch-utils';
import { useStateFunction } from '../../utils/hooks/useData';
import { makeMemoizedGetter } from '../../utils/data';
import { snapToMonday, SHORT_DATE_FORMAT } from '../../utils/date-utils';

import './Calendar.scss';

const ROW_HEIGHT = 110;
const LEFT_WIDTH = 90;
const COLUMN_WIDTH = 280;
const TOP_HEIGHT = 100;
const HEADER_HEIGHT = 100;
const DATE_FORMAT = 'yyyy-MM-dd';

interface CompanyInfo {
  mediaTypes: string[];
  color: string;
}
type MasterCompanyInfo = Record<string, CompanyInfo>;

interface MediaWeekData {
  booked?: number;
  expected?: number;
  target?: number;
}
// calendarData[company][mediaType][week]
type CalendarData = Record<string, Record<string, Record<string, MediaWeekData>>>;

interface Holiday {
  date: string;
  summary: string;
  url: string;
}

interface GetMediaSummaryResponse {
  data: CalendarData;
  calendarCompanies: MasterCompanyInfo;
}

interface FilterLine {
  cid: string;
  mediaTypes: string[];
}

interface CalendarCell {
  company: string;
  week: string;
  media: Record<string, MediaWeekData>;
}

const normalizeSpend = (spend: number | undefined): string =>
  `$${Math.round((spend ?? 0) / 1000)}K`;

const startOfThisWeek = R.pipe(Dfns.startOfISOWeek, Dfns.format(DATE_FORMAT))(new Date());

const getIsCurrentWeek = makeMemoizedGetter<string, boolean>({
  calculate: date => {
    const nextWeek = R.pipe(Dfns.parseISO, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(date);
    const twoWeeksAgo = R.pipe(Dfns.subWeeks(2), Dfns.format(DATE_FORMAT))(new Date());
    return date <= twoWeeksAgo && nextWeek > twoWeeksAgo;
  }
});

const inWarningRange = (date: string): boolean => {
  const currentWeek = R.pipe(Dfns.startOfISOWeek, Dfns.format(DATE_FORMAT))(new Date());
  const nextWeek = R.pipe(
    Dfns.startOfISOWeek,
    Dfns.addWeeks(1),
    Dfns.format(DATE_FORMAT)
  )(new Date());
  return date >= currentWeek && date <= nextWeek;
};

const filterOptions: FilterColumn[] = [
  { name: 'cid', label: 'Company' },
  { name: 'mediaTypes', label: 'Media' }
];

export const Calendar = () => {
  const setError = useSetError();
  const [masterCompanyInfo, setMasterCompanyInfo] = useState<MasterCompanyInfo>();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const hasFetchedHolidays = useRef(false);
  const [calendarData, setCalendarData] = useState<CalendarData>({});
  const [filter, setFilter] = useStateFunction<(line: FilterLine) => boolean>(() => true);

  // Fetch calendar data
  useEffect(() => {
    if (!masterCompanyInfo) {
      (async () => {
        try {
          const res = await ToolsLambdaFetch('/getMediaSummary');
          const { data, calendarCompanies } = await awaitJSON<GetMediaSummaryResponse>(res);
          setMasterCompanyInfo(calendarCompanies);
          setCalendarData(data);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [setError, masterCompanyInfo]);

  // Fetch holidays once. Gate on a ref rather than holidays.length: an empty ([])
  // response would store a fresh array reference each run, re-triggering this effect
  // and refetching forever.
  useEffect(() => {
    if (hasFetchedHolidays.current) return;
    hasFetchedHolidays.current = true;
    (async () => {
      try {
        const res = await SheetsLambdaFetch('/holidays');
        const data = await awaitJSON<Holiday[]>(res);
        setHolidays(data);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      }
    })();
  }, [setError]);

  const filterBarLines = useMemo<FilterLine[]>(
    () =>
      Object.keys(masterCompanyInfo ?? {})
        .filter(cid => !TEST_COMPANIES.includes(cid))
        .map(cid => ({
          cid,
          mediaTypes: [...(masterCompanyInfo?.[cid].mediaTypes ?? [])].sort()
        })),
    [masterCompanyInfo]
  );

  const { leftData, topData, tableData } = useMemo<{
    leftData?: string[];
    topData?: string[];
    tableData?: CalendarCell[][];
  }>(() => {
    if (!masterCompanyInfo) {
      return {};
    }
    const weekSet: Record<string, boolean> = {};

    for (const [company, mediaGroup] of Object.entries(calendarData)) {
      if (TEST_COMPANIES.includes(company)) {
        continue;
      }
      for (const mediaType of Object.values(mediaGroup)) {
        for (const week of Object.keys(mediaType)) {
          weekSet[week] = true;
        }
      }
    }

    const nextTopData = filterBarLines
      .filter(filter)
      .map(line => line.cid)
      .sort();

    const nextLeftData = Object.keys(weekSet).sort();

    const nextTableData: CalendarCell[][] = nextLeftData.map(week =>
      nextTopData.map(company => {
        const companyInfo = calendarData[company];
        const media: Record<string, MediaWeekData> = {};
        if (companyInfo) {
          for (const mediaType of Object.keys(companyInfo)) {
            const value = companyInfo[mediaType]?.[week];
            if (value) {
              media[mediaType] = value;
            }
          }
        }
        return { company, week, media };
      })
    );

    return { leftData: nextLeftData, topData: nextTopData, tableData: nextTableData };
  }, [calendarData, masterCompanyInfo, filter, filterBarLines]);

  // Renderers are defined at module scope (below) to keep them out of the
  // component body; the two that need data are built via factories here.
  const leftRenderer = useMemo(() => makeLeftRenderer(holidays), [holidays]);
  const cellRenderer = useMemo(() => makeCellRenderer(masterCompanyInfo), [masterCompanyInfo]);

  // When dates load, scroll to the current week
  const stickyTableScrollToRef = useRef<(row: number, col: number) => void>(null);
  const [scrolled, setScrolled] = useState(false);
  useLayoutEffect(() => {
    if (
      leftData &&
      leftData.length > 0 &&
      tableData &&
      !scrolled &&
      stickyTableScrollToRef.current
    ) {
      for (let i = 0; i < leftData.length; ++i) {
        if (getIsCurrentWeek(leftData[i])) {
          setScrolled(true);
          stickyTableScrollToRef.current?.(i, 0);
          break;
        }
      }
    }
  }, [leftData, tableData, scrolled]);

  return (
    <Page
      title='Calendar'
      pageType='Calendar'
      actions={
        <div className='calendar-page-actions'>
          <InfoTooltip iconColor='white'>
            <strong>Booked: </strong>The total amount of media we&apos;ve booked. For linear, this
            is in gross.
            <br />
            <br />
            <strong>Target: </strong>The total amount of media we should have booked in order to
            spend to expectation. For linear media, this incorporates our clearance forecast.
            <br />
            <br />
            <strong>Expected: </strong>Client&apos;s desired spend level.
          </InfoTooltip>
          <div className='calendar-legend'>Booked / Target (Expected)</div>
        </div>
      }
    >
      <div className='calendar-page' data-testid='calendar-page'>
        <div className='calendar-filter-bar' data-testid='calendar-filter-bar'>
          <FilterBar
            size={Size.LG}
            options={filterOptions}
            lines={filterBarLines}
            onFilter={setFilter}
          />
        </div>
        <div className='calendar-table' data-testid='calendar-table'>
          {tableData && leftData ? (
            <AutoSizer
              renderProp={({ width, height }) => (
                <StickyTable
                  scrollToRef={stickyTableScrollToRef}
                  alternateColors={false}
                  width={width ?? 0}
                  height={height ?? 0}
                  leftWidth={LEFT_WIDTH}
                  rowHeight={ROW_HEIGHT}
                  columnWidth={COLUMN_WIDTH}
                  topHeight={TOP_HEIGHT}
                  data={tableData}
                  topData={topData}
                  leftData={leftData}
                  cornerRenderer={cornerRenderer}
                  topRenderer={topRenderer}
                  leftRenderer={leftRenderer}
                  cellRenderer={cellRenderer}
                />
              )}
            />
          ) : (
            <Skeleton>
              <TableSkeleton rowHeight={ROW_HEIGHT} headerHeight={HEADER_HEIGHT} />
            </Skeleton>
          )}
        </div>
      </div>
    </Page>
  );
};

const MediaTypeIcon = ({ mediaType }: { mediaType: string }) => {
  switch (mediaType) {
    case 'Tv':
      return <MdTv />;
    case 'Streaming':
      return <FiCast />;
    case 'Audio':
      return <MdHeadset />;
    case 'Radio':
      return <MdRadio />;
    default:
      return <MdImage />;
  }
};

// The table has a single (top-left) corner; render the "Week" label for it.
const cornerRenderer = () => (
  <div className='calendar-corner-header'>
    <div>Week</div>
  </div>
);

// Company logo header; falls back to the client name if the logo fails to load.
const topRenderer: CellRenderer<string> = ({ data, style, classes }) => (
  <OverlayTrigger placement={Placement.BOTTOM} overlay={<Tooltip>{data}</Tooltip>}>
    <div className={cn(classes, 'top-data-cell')} style={style}>
      <div className='logo'>
        <Img
          src={`https://cdn.blisspointmedia.com/companies/${data}/logo.png`}
          unloader={<span className='logo-fallback'>{data}</span>}
        />
      </div>
    </div>
  </OverlayTrigger>
);

const makeLeftRenderer = (holidays: Holiday[]): CellRenderer<string> => {
  const leftRenderer: CellRenderer<string> = ({ data, style, classes }) => {
    const holidayInfo = holidays.find(holiday => snapToMonday(holiday.date) === data);

    return (
      <div
        className={cn(classes, 'left-week', {
          'current-week current-week-date-style': data === startOfThisWeek
        })}
        style={style}
      >
        <div>{Dfns.format(SHORT_DATE_FORMAT, Dfns.parseISO(data))}</div>
        {holidayInfo && (
          <OverlayTrigger
            placement='right'
            overlay={<Tooltip>{`${holidayInfo.summary} ${holidayInfo.date}`}</Tooltip>}
          >
            <Img
              className='calendar-holiday-logo'
              src={`https://cdn.blisspointmedia.com/assets/img/holidays/${holidayInfo.url}`}
            />
          </OverlayTrigger>
        )}
      </div>
    );
  };

  return leftRenderer;
};

const makeCellRenderer = (
  masterCompanyInfo: MasterCompanyInfo | undefined
): CellRenderer<CalendarCell> => {
  const cellRenderer: CellRenderer<CalendarCell> = ({ data, style, classes }) => {
    const values: Record<string, MediaWeekData> = {};
    for (const mediaType of masterCompanyInfo?.[data.company]?.mediaTypes ?? []) {
      const ourData = data.media[mediaType];
      if (!ourData) {
        continue;
      }
      const hasBooked = ourData.booked != null && ourData.booked !== 0;
      const hasExpected = ourData.expected != null && ourData.expected !== 0;
      if (hasBooked || hasExpected) {
        values[mediaType] = ourData;
      }
    }

    const cellClasses = cn(classes, {
      'current-week': data.week === startOfThisWeek
    });

    if (!Object.keys(values).length) {
      return <div className={cellClasses} style={style} />;
    }

    const companyColor = masterCompanyInfo?.[data.company]?.color;

    return (
      <div className={cn(cellClasses, 'content-cell')} style={style}>
        <div className='content-box' style={{ borderLeftColor: companyColor }}>
          {Object.keys(values)
            .sort()
            .map(mediaType => {
              const { booked, expected, target } = values[mediaType];

              let warning = false;
              if (booked != null && target != null && target !== 0 && inWarningRange(data.week)) {
                warning = Math.abs((booked - target) / target) > 0.2;
              }

              const mediaTypeCapitalized = mediaType.charAt(0).toUpperCase() + mediaType.slice(1);
              const mediaLabel = mediaTypeCapitalized === 'Tv' ? 'Linear' : mediaTypeCapitalized;

              return (
                <div className='content-box-row' key={mediaType}>
                  <div className='media-label'>
                    <MediaTypeIcon mediaType={mediaTypeCapitalized} />
                    <span>{mediaLabel}</span>
                    {warning && (
                      <span className='calendar-warning'>
                        <MdError />
                        Off target
                      </span>
                    )}
                  </div>
                  <div className='media-values'>
                    <strong>{normalizeSpend(booked)}</strong>
                    <span>{` / ${normalizeSpend(target)} (${normalizeSpend(expected)})`}</span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return cellRenderer;
};
